import React, {
  Fragment,
  useEffect,
  useState,
  useRef,
  useCallback,
  useContext,
  useMemo,
} from "react";
import Webcam from "react-webcam";
import { Chip, Dialog, DialogContent, Slide } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import {
  Button as ButtonAntd,
  ConfigProvider,
  message,
  Popconfirm,
} from "antd";
import {
  BulbTwoTone,
  CloseCircleOutlined,
  InteractionTwoTone,
  PauseCircleFilled,
  PlayCircleFilled,
  ReloadOutlined,
  SaveFilled,
} from "@ant-design/icons";
import { useScreenSize } from "../../../hooks";
import { css } from "@emotion/css";

interface VideoComponentPropsI {
  open: boolean;
  handleClose: () => void;
  setVideo: (video: string) => void;
  elvideo: string;
  time: number;
  timeString: string;
  title: string;
  audioBit?: number;
  videoBit?: number;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export function VideoComponentOriginal({
  open,
  handleClose,
  setVideo,
  elvideo,
  time = 60000,
  timeString = "1min",
  title,
  audioBit = 95000,
  videoBit = 2000000,
}: VideoComponentPropsI) {
  const { width } = useScreenSize();
  const webcamRef = useRef<any>(null);

  const messageRef = useRef<any>(null);
  const mediaRecorderRef = useRef<any>(null);
  const [capturing, setCapturing] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<any[]>([]);
  const [processingVideo, setProcessingVideo] = useState(false);
  const [counter, setCounter] = useState(0);
  const { getPrefixCls } = useContext(ConfigProvider.ConfigContext);
  const rootPrefixCls = getPrefixCls();
  const [activeDeviceId, setActiveDeviceId] = useState<string | undefined>(
    undefined
  );
  const [videoConstraints, setVideoConstraints] = useState<any>({
    deviceId: activeDeviceId,
    facingMode: "environment",
    autoFocus: "continuous",
    whiteBalance: "continuous",
    frameRate: 30,
    zoom: 0,
    focusDepth: 0,
    width: { ideal: 1280 },
    height: { ideal: 720 },
    torch: false,
  } as unknown as any);
  const [continuoChange, setContinuoChange] = useState<boolean>(false);
  const [numberOfCameras, setNumberOfCameras] = useState(0);
  const [mirror, setMirror] = useState(false);
  const [isChangingCamera, setChangingCamera] = useState<boolean>(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    (async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((i) => i.kind == "videoinput");
      setNumberOfCameras(videoDevices.length);
    })();
    return () => {
      webcamRef.current! = null;
      setNumberOfCameras(0);
      setActiveDeviceId(undefined);
      setCounter(0);
    };
  }, []);

  useEffect(() => {
    if (continuoChange) {
      setContinuoChange(false);
      setChangingCamera(false);
    }
  }, [continuoChange]);

  // Actualizar el contador de segundos
  useEffect(() => {
    let timer: any;
    if (capturing && !paused) {
      timer = setInterval(() => {
        setCounter((prev) => prev + 1);
      }, 1000);
    } else if (!capturing || paused) {
      clearInterval(timer);
    }
    return () => clearInterval(timer);
  }, [capturing, paused, counter]);

  const handleDataAvailable = useCallback(
    ({ data }: { data: Blob }) => {
      if (data.size > 0) {
        setRecordedChunks((prev) => prev.concat(data));
      }
    },
    [setRecordedChunks]
  );

  const handleStartCaptureClick = useCallback(() => {
    setCapturing(true);
    setRecordedChunks([]);
    setCounter(0);
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: "video/mp4", // Código de video
      audioBitsPerSecond: audioBit, // Bits por segundo
      videoBitsPerSecond: videoBit, // Bits por segundo
    });
    mediaRecorderRef.current.addEventListener(
      "dataavailable",
      handleDataAvailable
    );
    mediaRecorderRef.current.start();
  }, [webcamRef, setCapturing, mediaRecorderRef, setRecordedChunks]);

  // Pausar la grabación
  const handlePauseCapture = useCallback(() => {
    mediaRecorderRef.current.pause();
    setPaused(true);
  }, [mediaRecorderRef, setPaused]);

  // Reanudar la grabación
  const handleResumeCapture = useCallback(() => {
    mediaRecorderRef.current.resume();
    setPaused(false);
  }, [mediaRecorderRef, setPaused]);

  // Detener la grabación
  const handleStopCapture = useCallback(() => {
    mediaRecorderRef.current.stop();
    setCapturing(false);
    setPaused(false); // Reseteamos el estado de pausa al detener
  }, [mediaRecorderRef, setCapturing]);

  const handleDownload = useCallback(async () => {
    setProcessingVideo(true);
    try {
      if (recordedChunks.length) {
        const blob = new Blob(recordedChunks, { type: "video/mp4" });
        setVideo(blob as unknown as string);
        setProcessingVideo(false);
        setRecordedChunks([]);
        handleClose();
      }
    } catch (error: any) {
      message.error(`Error processing video: ${error.toString()}`, 5);
    } finally {
      setProcessingVideo(false);
    }
  }, [recordedChunks]);

  const linearGradientButton = css`
    &.${rootPrefixCls}-btn-primary:not([disabled]):not(
        .${rootPrefixCls}-btn-dangerous
      ) {
      border-width: 0;

      > span {
        position: relative;
      }

      &::before {
        content: "";
        background: linear-gradient(135deg, #08b806, #7fffd4);
        position: absolute;
        inset: 0;
        opacity: 1;
        transition: all 0.3s;
        border-radius: inherit;
      }

      &:hover::before {
        opacity: 0.7;
      }
    }
  `;

  const linearGradientButtonClear = css`
    &.${rootPrefixCls}-btn-primary:not([disabled]):not(
        .${rootPrefixCls}-btn-dangerous
      ) {
      border-width: 0;

      > span {
        position: relative;
      }

      &::before {
        content: "";
        background: linear-gradient(135deg, #b22222, #f08080);
        position: absolute;
        inset: 0;
        opacity: 1;
        transition: all 0.3s;
        border-radius: inherit;
      }

      &:hover::before {
        opacity: 0.8;
      }
    }
  `;

  const handleTorch = () => {
    if (videoConstraints.facingMode === "user") return;
    message.info(videoConstraints.torch ? "Turn Off Flash" : "Trun On Flash");
    setChangingCamera(true);
    setVideoConstraints((constraints: any) => ({
      ...constraints,
      torch: !constraints.torch,
    }));
    setContinuoChange(true);
  };

  const changeFacingMode = () => {
    message.info("Cambiando el modo de cámara");
    setChangingCamera(true);
    if (videoConstraints.facingMode === "environment") {
      setMirror(true);
    } else {
      setMirror(false);
    }
    setVideoConstraints((constraints: any) => ({
      ...constraints,
      facingMode: constraints.facingMode === "user" ? "environment" : "user",
    }));
    setContinuoChange(true);
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        overflowY: "hidden",
        overflowX: "hidden",
        p: 0,
        m: 0,
      }}
    >
      <DialogContent
        sx={{
          overflowY: "hidden",
          overflowX: "hidden",
          p: 0,
          m: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        {elvideo ? null : (
          <ButtonAntd
            style={{
              position: "absolute",
              bottom: 15,
              left: width / 2 - 87,
              zIndex: 2,
            }}
            size="large"
            disabled={numberOfCameras < 2 || capturing}
            onClick={changeFacingMode}
            type="dashed"
            shape="circle"
            icon={
              <InteractionTwoTone
                twoToneColor={numberOfCameras < 2 ? "#FFD700" : "#FF0000"}
              />
            }
          />
        )}
        {elvideo ? null : (
          <ButtonAntd
            style={{
              position: "absolute",
              top: 1,
              left: 1,
              zIndex: 2,
            }}
            size="large"
            onClick={handleTorch}
            type="dashed"
            shape="circle"
            disabled={capturing}
            icon={
              <BulbTwoTone
                twoToneColor={videoConstraints.torch ? "#FFD700" : "#FF0000"}
              />
            }
          />
        )}
        {elvideo ? (
          <VideoPlayer
            elvideo={elvideo}
            handleClose={handleClose}
            setVideo={setVideo}
          />
        ) : (
          <Fragment>
            <ButtonAntd
              style={{
                position: "absolute",
                top: 5,
                right: 5,
                zIndex: 1601,
              }}
              size="large"
              onClick={handleClose}
              type="primary"
              danger
              icon={<CloseCircleOutlined />}
            />

            {isChangingCamera ? null : (
              <Webcam
                height="100%"
                audio={true}
                mirrored={mirror}
                ref={webcamRef}
                // screenshotQuality={0.8}
                videoConstraints={videoConstraints}
                style={{ minHeight: "100%" }}
                muted={true}
                // screenshotFormat="image/webp"
              />
            )}
            {capturing ? (
              <Fragment>
                <Chip
                  color="error"
                  label={counter}
                  size="medium"
                  sx={{
                    position: "absolute",
                    top: 5,
                    left: 50,
                    zIndex: 1601,
                    width: 50,
                  }}
                />
                <ButtonAntd
                  style={{
                    position: "absolute",
                    bottom: 10,
                    left: width / 2 - 110,
                    width: 70,
                    height: 70,
                    zIndex: 100,
                  }}
                  size="large"
                  onClick={paused ? handleResumeCapture : handlePauseCapture}
                  type="dashed"
                  shape="circle"
                  icon={paused ? <PlayCircleFilled /> : <PauseCircleFilled />}
                />
                <ButtonAntd
                  style={{
                    position: "absolute",
                    bottom: 10,
                    left: width / 2 - 35,
                    width: 70,
                    height: 70,
                  }}
                  size="large"
                  onClick={handleStopCapture}
                  type="primary"
                  danger
                  shape="circle"
                  icon={<p>⬜️</p>}
                />
              </Fragment>
            ) : (
              <ButtonAntd
                style={{
                  position: "absolute",
                  bottom: 10,
                  left: width / 2 - 35,
                  width: 70,
                  height: 70,
                }}
                size="large"
                onClick={handleStartCaptureClick}
                type="primary"
                shape="circle"
                // loading={
                //   title === "videoPanelExterior" && processingVideoPE
                //     ? true
                //     : title === "videoCajas" && processingVideoC
                //     ? true
                //     : title === "videoCierreContenedor" && processingVideoCC
                //     ? true
                //     : false
                // }
                icon={<p>🔴</p>}
                // disabled={
                //   title === "videoPanelExterior" && processingVideoPE
                //     ? true
                //     : title === "videoCajas" && processingVideoC
                //     ? true
                //     : title === "videoCierreContenedor" && processingVideoCC
                //     ? true
                //     : recordedChunks.length > 0
                //     ? true
                //     : false
                // }
              />
            )}
            {recordedChunks.length > 0 ? (
              <Fragment>
                <ConfigProvider
                  button={{
                    className: linearGradientButtonClear,
                  }}
                >
                  <Popconfirm
                    title="Limpiar"
                    description="Se borrará todo el video"
                    onConfirm={() => setRecordedChunks([])}
                    // onCancel={handleValidate}
                    okText="Si"
                    cancelText="No"
                    zIndex={1601}
                  >
                    <ButtonAntd
                      style={{
                        position: "absolute",
                        bottom: 20,
                        left: width / 2 + 110,
                        width: 50,
                        height: 50,
                        zIndex: 1601,
                      }}
                      size="large"
                      type="primary"
                      icon={<ReloadOutlined size={40} />}
                    />
                  </Popconfirm>
                </ConfigProvider>
                <ConfigProvider
                  button={{
                    className: linearGradientButton,
                  }}
                >
                  <ButtonAntd
                    style={{
                      position: "absolute",
                      bottom: 20,
                      left: width / 2 + 50,
                      width: 50,
                      height: 50,
                      zIndex: 1601,
                    }}
                    size="large"
                    onClick={handleDownload}
                    type="primary"
                    loading={processingVideo}
                    icon={<SaveFilled size={40} />}
                  />
                </ConfigProvider>
              </Fragment>
            ) : null}
          </Fragment>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface VideoPlayer {
  elvideo: string;
  handleClose: () => void;
  setVideo: (video: any) => void;
}

const VideoPlayer = ({ elvideo, handleClose, setVideo }: VideoPlayer) => {

  const handleClear = () => {
    setVideo("");
  };


  return (
    <Fragment>
      <ButtonAntd
        style={{
          position: "absolute",
          top: 5,
          right: 5,
          zIndex: 1601,
        }}
        size="large"
        onClick={handleClose}
        type="primary"
        danger
        icon={<CloseCircleOutlined />}
      />
      <ButtonAntd
        style={{
          position: "absolute",
          top: 5,
          right: 55,
          zIndex: 1601,
        }}
        size="large"
        onClick={handleClear}
        type="primary"
        shape="circle"
        icon={<ReloadOutlined />}
      />
    <video controls style={{ width: "100%", height: "100vh" }}>
        <source type="video/mp4" src={elvideo} />
      </video>
    </Fragment>
  );
};
